import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Sparkles, Target, Flame, Trophy, Medal, Zap, Rocket,
  Crosshair, Star, Compass, Lock,
} from "lucide-react";

const ICONS = {
  sparkles: Sparkles, target: Target, flame: Flame, trophy: Trophy,
  medal: Medal, zap: Zap, rocket: Rocket, crosshair: Crosshair,
  star: Star, compass: Compass,
};

const TIER_COLORS = {
  bronze: { bg: "bg-[#FFF7ED]", border: "border-[#D97706]", text: "text-[#B45309]" },
  silver: { bg: "bg-[#F3F4F6]", border: "border-[#6B7280]", text: "text-[#374151]" },
  gold:   { bg: "bg-[#FEFCE8]", border: "border-[#CA8A04]", text: "text-[#854D0E]" },
};

export function BadgeTile({ badge, small = false }) {
  const Icon = ICONS[badge.icon] || Sparkles;
  const c = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;
  const size = small ? "p-3" : "p-4";
  if (!badge.earned) {
    return (
      <div className={`border border-black/10 ${size} bg-neutral-50 opacity-60`} data-testid={`badge-locked-${badge.id}`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 border border-black/20 flex items-center justify-center bg-white">
            <Lock className="w-3.5 h-3.5 text-neutral-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black tracking-tight truncate">{badge.title}</div>
            {!small && <div className="text-[10px] font-mono text-neutral-500 mt-0.5 leading-tight">{badge.desc}</div>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`border-2 ${c.border} ${c.bg} ${size} relative`} data-testid={`badge-earned-${badge.id}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 border-2 ${c.border} flex items-center justify-center bg-white ${c.text}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black tracking-tight truncate">{badge.title}</div>
          {!small && <div className="text-[10px] font-mono text-neutral-600 mt-0.5 leading-tight">{badge.desc}</div>}
        </div>
      </div>
      <div className={`absolute top-1 right-1 text-[8px] uppercase tracking-widest font-bold ${c.text}`}>{badge.tier}</div>
    </div>
  );
}

export default function BadgesGrid({ limit, showViewAll = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/badges").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const visible = limit ? data.badges.slice(0, limit) : data.badges;

  return (
    <div data-testid="badges-grid">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-1">Achievements</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight">
            {data.earned_count} <span className="text-muted-foreground">/ {data.total} unlocked</span>
          </h2>
        </div>
        {showViewAll && (
          <Link to="/badges" className="text-xs uppercase tracking-widest font-bold text-brand-gold hover:underline" data-testid="badges-view-all">
            View all →
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {visible.map((b) => (
          <BadgeTile key={b.id} badge={b} small />
        ))}
      </div>
    </div>
  );
}
